import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { fetchBooks, fetchProfile } from "../api";
import { clearStoredAuthToken, getStoredAuthToken, setStoredAuthToken } from "../authStorage";

import HomePage from "./Home";
import StorePage from "./StorePage";
import Navbar from "../layouts/Navbar";
import BookDetail from "./BookDetails";
import CartPage from "./CartPage";
import CheckoutPage from "./CheckoutPage";
import Footer from "../layouts/Footer";
import AboutUs from "./AboutUs";
import ContactUs from "./ContactUs";
import DealersNetwork from "./DealersNetwork";
import SignInPage from "./SignInPage";
import RegisterPage from "./RegisterPage";
import AccountPage from "./AccountPage";
import AdminPanel from "./AdminPanel";
import AdminUploadPage from "./AdminUploadPage";
import AdminUpdatePage from "./AdminUpdatePage";
import AdminRoute from "../components/AdminRoute";
import MyOrdersPage from "./MyOrdersPage";
import OrderDetailPage from "./OrderDetailPage";
import AddressesPage from "./AddressesPage";
import WishlistPage from "./WishlistPage";
import PaymentResponsePage from "./PaymentResponsePage";
import RegisterMailUpdatePage from "./RegisterMailUpdatePage";

// ══════════════════════════════════════════════════════
// GLOBAL STYLES
// ══════════════════════════════════════════════════════
const G = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;0,900;1,400;1,700&family=Nunito:wght@300;400;500;600;700;800&family=Nunito+Sans:wght@300;400;600;700&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}

:root{
  --cream:#FBF8F3;
  --cream2:#F5F0E8;
  --cream3:#EDE6D6;
  --paper:#FFFEF9;
  --forest:#1C3A2A;
  --forest2:#2A5240;
  --forest3:#3D6B54;
  --gold:#C8960A;
  --gold2:#E8B422;
  --gold3:#F5D067;
  --terra:#D4622A;
  --terra2:#E87A46;
  --rose:#C94B6A;
  --sky:#2B7EC1;
  --ink:#0F1B14;
  --ink2:#243320;
  --text:#1A2E1D;
  --text2:#3D5244;
  --text3:#6B7D6F;
  --text4:#9BA89E;
  --border:#DDD5C3;
  --border2:#C8BEA8;
  --shadow:0 2px 12px rgba(28,58,42,0.08);
  --shadow-md:0 6px 28px rgba(28,58,42,0.12);
  --shadow-lg:0 16px 56px rgba(28,58,42,0.18);
  --shadow-xl:0 32px 80px rgba(28,58,42,0.22);
  --r:14px;
  --r-lg:22px;
}

html{scroll-behavior:smooth;}
body{font-family:'Nunito Sans',sans-serif;background:var(--cream);color:var(--text);-webkit-font-smoothing:antialiased;overflow-x:hidden;}
h1,h2,h3,h4{font-family:'Playfair Display',serif;line-height:1.15;}
::-webkit-scrollbar{width:5px;}
::-webkit-scrollbar-track{background:var(--cream2);}
::-webkit-scrollbar-thumb{background:var(--forest3);border-radius:3px;}

/* ─── ANIMATIONS ─── */
@keyframes fadeUp{from{opacity:0;transform:translateY(32px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes slideRight{from{opacity:0;transform:translateX(-40px);}to{opacity:1;transform:translateX(0);}}
@keyframes float{0%,100%{transform:translateY(0px) rotate(-2deg);}50%{transform:translateY(-12px) rotate(1deg);}}
@keyframes float2{0%,100%{transform:translateY(0px) rotate(3deg);}50%{transform:translateY(-8px) rotate(-1deg);}}
@keyframes float3{0%,100%{transform:translateY(0px) rotate(-1deg);}50%{transform:translateY(-16px) rotate(2deg);}}
@keyframes shimmer{0%{background-position:-200% 0;}100%{background-position:200% 0;}}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.5;}}
@keyframes spin{from{transform:rotate(0deg);}to{transform:rotate(360deg);}}
@keyframes marquee{0%{transform:translateX(0);}100%{transform:translateX(-50%);}  }
@keyframes bookFlip{0%{transform:rotateY(0deg);}100%{transform:rotateY(-20deg);}}
@keyframes slideCarousel{from{transform:translateX(var(--from));}to{transform:translateX(var(--to));}}
@keyframes countUp{from{opacity:0;transform:scale(0.7);}to{opacity:1;transform:scale(1);}}
@keyframes glow{0%,100%{box-shadow:0 0 20px rgba(200,150,10,0.3);}50%{box-shadow:0 0 40px rgba(200,150,10,0.6),0 0 60px rgba(200,150,10,0.3);}}
@keyframes badgePop{0%{transform:scale(0) rotate(-15deg);}60%{transform:scale(1.2) rotate(5deg);}100%{transform:scale(1) rotate(0deg);}}
@keyframes dealTick{from{width:100%;}to{width:0%;}}
@keyframes waveBook{0%{transform:rotate(-5deg) scale(1);}25%{transform:rotate(0deg) scale(1.05);}50%{transform:rotate(5deg) scale(1);}75%{transform:rotate(0deg) scale(1.05);}100%{transform:rotate(-5deg) scale(1);}}

.anim-fade-up{animation:fadeUp 0.6s cubic-bezier(0.16,1,0.3,1) both;}
.anim-fade-up-d1{animation:fadeUp 0.6s 0.1s cubic-bezier(0.16,1,0.3,1) both;}
.anim-fade-up-d2{animation:fadeUp 0.6s 0.2s cubic-bezier(0.16,1,0.3,1) both;}
.anim-fade-up-d3{animation:fadeUp 0.6s 0.3s cubic-bezier(0.16,1,0.3,1) both;}
.anim-fade-up-d4{animation:fadeUp 0.6s 0.4s cubic-bezier(0.16,1,0.3,1) both;}
.anim-slide-right{animation:slideRight 0.7s cubic-bezier(0.16,1,0.3,1) both;}

/* ─── LOADER ─── */
.global-loader-container {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;        /* ← was "items-center" (typo) */
  justify-content: center;
  background: var(--cream);
  z-index: 9999;
  gap: 28px;
}
.global-loader-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.global-loader-brand h2 {
  font-family: 'Playfair Display', serif;
  font-size: 20px;
  font-weight: 800;
  color: var(--forest);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.global-loader-brand p {
  font-size: 12px;
  color: var(--text3);
  font-weight: 500;
  letter-spacing: 0.06em;
}
.global-loader-books {
  display: flex;
  align-items: flex-end;
  gap: 5px;
  height: 44px;
}
.global-loader-books span {
  display: block;
  width: 9px;
  border-radius: 2px 2px 0 0;
  animation: loaderBar 1.1s ease-in-out infinite;
  transform-origin: bottom;
}
.global-loader-books span:nth-child(1) { height: 28px; background: var(--forest);  animation-delay: 0s; }
.global-loader-books span:nth-child(2) { height: 38px; background: var(--gold);    animation-delay: 0.15s; }
.global-loader-books span:nth-child(3) { height: 44px; background: var(--forest2); animation-delay: 0.3s; }
.global-loader-books span:nth-child(4) { height: 32px; background: var(--terra);   animation-delay: 0.45s; }
.global-loader-books span:nth-child(5) { height: 40px; background: var(--forest3); animation-delay: 0.6s; }
.global-loader-track {
  width: 160px;
  height: 3px;
  background: var(--cream3);
  border-radius: 99px;
  overflow: hidden;
}
.global-loader-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, var(--forest), var(--gold));
  animation: loaderSlide 1.6s ease-in-out infinite;
}
@keyframes loaderBar {
  0%, 100% { transform: scaleY(1);   opacity: 1; }
  50%       { transform: scaleY(0.5); opacity: 0.5; }
}
@keyframes loaderSlide {
  0%   { width: 0%;   margin-left: 0%; }
  50%  { width: 70%;  margin-left: 15%; }
  100% { width: 0%;   margin-left: 100%; }
}

/* ─── BUTTONS ─── */
.btn{display:inline-flex;align-items:center;gap:8px;padding:12px 26px;border-radius:50px;font-family:'Nunito Sans',sans-serif;font-weight:700;font-size:14px;cursor:pointer;border:none;transition:all 0.22s;letter-spacing:0.01em;}
.btn-forest{background:var(--forest);color:white;}.btn-forest:hover{background:var(--forest2);transform:translateY(-2px);box-shadow:0 8px 24px rgba(28,58,42,0.35);}
.btn-gold{background:var(--gold);color:white;}.btn-gold:hover{background:var(--gold2);transform:translateY(-2px);box-shadow:0 8px 24px rgba(200,150,10,0.4);}
.btn-terra{background:var(--terra);color:white;}.btn-terra:hover{background:var(--terra2);transform:translateY(-2px);box-shadow:0 8px 24px rgba(212,98,42,0.4);}
.btn-outline{background:transparent;color:var(--forest);border:2px solid var(--forest);}.btn-outline:hover{background:var(--forest);color:white;transform:translateY(-2px);}
.btn-ghost{background:transparent;color:var(--text2);border:1.5px solid var(--border);padding:10px 20px;border-radius:50px;font-family:'Nunito Sans',sans-serif;font-weight:600;font-size:13px;cursor:pointer;transition:all 0.2s;}.btn-ghost:hover{border-color:var(--forest);color:var(--forest);background:var(--cream2);}

/* ─── CARD HOVER ─── */
.book-card-wrap{transition:transform 0.28s cubic-bezier(0.34,1.56,0.64,1),box-shadow 0.28s ease;}
.book-card-wrap:hover{transform:translateY(-8px) scale(1.01);box-shadow:var(--shadow-lg);}

/* ─── BADGE ─── */
.badge{display:inline-flex;align-items:center;padding:3px 10px;border-radius:50px;font-size:11px;font-weight:800;letter-spacing:0.05em;text-transform:uppercase;}

/* ─── SECTION ─── */
.section{padding:80px 72px;}
.section-sm{padding:56px 72px;}

/* ─── SHIMMER SKELETON ─── */
.shimmer{background:linear-gradient(90deg,var(--cream2) 25%,var(--cream3) 50%,var(--cream2) 75%);background-size:200% 100%;animation:shimmer 1.4s infinite;}

/* ─── CAROUSEL TRACK ─── */
.carousel-track{display:flex;gap:22px;transition:transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94);}
.carousel-wrap{overflow:hidden;width:100%;}

/* ─── RESPONSIVE ─── */
@media (max-width: 1024px) {
  .section{padding:60px 40px;}
  .section-sm{padding:40px 40px;}
  h1{font-size:32px;}
  h2{font-size:28px;}
}
@media (max-width: 768px) {
  .section{padding:40px 24px;}
  .section-sm{padding:30px 24px;}
  h1{font-size:28px;}
  h2{font-size:24px;}
  h3{font-size:20px;}
}
@media (max-width: 480px) {
  .section{padding:32px 16px;}
  .section-sm{padding:24px 16px;}
}

.mobile-nav-only { display: none; }
@media (max-width: 1024px) {
  .mobile-nav-only { display: block; }
  .desktop-nav-only { display: none; }
}
`;


function AppInner() {
	const navigate = useNavigate();
	const [books, setBooks] = useState([]);
	const [selectedBook, setSelectedBook] = useState(null);
	const [cart, setCart] = useState([]);
	const [user, setUser] = useState(null);
	const [authToken, setAuthToken] = useState(() => getStoredAuthToken());
	const [wishlist, setWishlist] = useState([]);
	const [isInitialLoading, setIsInitialLoading] = useState(true);

	// Fetch critical app data on mount
	useEffect(() => {
		const bootstrap = async () => {
			setIsInitialLoading(true);
			try {
				const promises = [fetchBooks()];
				if (authToken) {
					promises.push(fetchProfile(authToken));
				}

				const results = await Promise.allSettled(promises);

				// Handle books
				if (results[0].status === 'fulfilled') {
					setBooks(results[0].value);
				}

				// Handle profile if exists
				if (results[1] && results[1].status === 'fulfilled') {
					const updatedUser = { ...results[1].value, token: authToken };
					setUser(updatedUser);
				} else if (authToken) {
					clearStoredAuthToken();
					setAuthToken("");
					setUser(null);
				}
			} catch (err) {
				console.error("App bootstrap error:", err);
			} finally {
				// Artificial delay for smooth transition (optional, user likes premium feels)
				setTimeout(() => setIsInitialLoading(false), 300);
			}
		};

		bootstrap();
	}, [authToken]); // Run once on startup and when auth changes

	const addCart = useCallback(book => {
		setCart(currentCart => currentCart.some(item => item._id === book._id) ? currentCart : [...currentCart, book]);
	}, []);

	const onView = useCallback(book => {
		setSelectedBook(book);
		navigate(`/book/${book._id}`);
		window.scrollTo(0, 0);
	}, [navigate]);

	const onLogin = useCallback(userData => {
		if (!userData?.token) return;
		setStoredAuthToken(userData.token);
		setAuthToken(userData.token);
		setUser(userData);
	}, []);

	const onLogout = useCallback(() => {
		setUser(null);
		setAuthToken("");
		clearStoredAuthToken();
	}, []);

	return (
		<div style={{ minHeight: "100vh", background: "var(--cream)" }}>
			{isInitialLoading && (
				<div className="global-loader-container">
					<div className="global-loader-books">
						<span></span><span></span><span></span><span></span><span></span>
					</div>
					<div className="global-loader-brand">
						<h2>BHARAT LAW HOUSE PVT. LTD.</h2>
						<p>Curating Legal Excellence...</p>
					</div>
					<div className="global-loader-track">
						<div className="global-loader-fill"></div>
					</div>
				</div>
			)}

			<div className="app-content-fade" style={{
				opacity: isInitialLoading ? 0 : 1,
				transition: "opacity 0.6s ease",
				pointerEvents: isInitialLoading ? "none" : "auto"
			}}>
				<Navbar cart={cart} user={user} onLogout={onLogout} books={books} />

				<Routes>
					<Route path='/' element={<HomePage books={books} onView={onView} cart={cart} addCart={addCart} />} />
					<Route path='/store' element={<StorePage books={books} onView={onView} cart={cart} addCart={addCart} />} />
					<Route path='/book/:id' element={<BookDetail book={selectedBook} cart={cart} addCart={addCart} allBooks={books} />} />
					<Route path='/cart' element={<CartPage cart={cart} setCart={setCart} />} />
					<Route path='/checkout' element={<CheckoutPage cart={cart} setCart={setCart} />} />

					<Route path='/about' element={<AboutUs />} />
					<Route path='/contact' element={<ContactUs />} />
					<Route path='/register-mail-update' element={<RegisterMailUpdatePage />} />
					<Route path='/update' element={<RegisterMailUpdatePage />} />
					<Route path='/dealers' element={<DealersNetwork />} />
					{/* Auth */}
					<Route path='/signin' element={<SignInPage onLogin={onLogin} />} />
					<Route path='/register' element={<RegisterPage onLogin={onLogin} />} />
					{/* Account */}
					<Route path='/account' element={<AccountPage user={user} setUser={setUser} onLogout={onLogout} />} />
					<Route path='/admin' element={
						<AdminRoute user={user} authReady={!isInitialLoading}>
							<AdminPanel user={user} />
						</AdminRoute>
					} />
					<Route path='/admin/upload' element={
						<AdminRoute user={user} authReady={!isInitialLoading}>
							<AdminUploadPage user={user} />
						</AdminRoute>
					} />
					<Route path='/admin/update/:id' element={
						<AdminRoute user={user} authReady={!isInitialLoading}>
							<AdminUpdatePage user={user} />
						</AdminRoute>
					} />
					<Route path='/orders' element={<MyOrdersPage user={user} onLogout={onLogout} />} />
					<Route path='/order/:id' element={<OrderDetailPage user={user} onLogout={onLogout} />} />
					<Route path='/addresses' element={<AddressesPage user={user} onLogout={onLogout} />} />
					<Route path='/wishlist' element={<WishlistPage user={user} onLogout={onLogout} wishlist={wishlist} setWishlist={setWishlist} onView={onView} addCart={addCart} />} />
					<Route path='/payment/response' element={<PaymentResponsePage setCart={setCart} />} />

				</Routes>
				<Footer />
			</div>
		</div>
	);
}

export default function App() {
	return (
		<>
			<style>{G}</style>
			<BrowserRouter>
				<AppInner />
			</BrowserRouter>
		</>
	);
}
